import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { TeacherBillingSubscriptionResponse } from "../auth/api-client";
import { lessonIconDefinition } from "./lessonIcons";

type KpiAccent = {
  readonly bg: string;
  readonly fg: string;
};

type KpiItem = {
  readonly id: string;
  readonly iconName: string;
  readonly accent: KpiAccent;
  readonly title: string;
  readonly used: number;
  readonly limit: number | null;
  readonly unit: string;
  readonly description: string;
  readonly perk?: string;
};

function usagePct(used: number, limit: number | null): number | null {
  if (limit == null || limit <= 0) return null;
  return Math.min(100, Math.round((used / limit) * 100));
}

function headroomLine(used: number, limit: number | null, unit: string): string | null {
  if (limit == null) return null;
  const remaining = limit - used;
  if (remaining <= 0) {
    return `At your ${unit} limit on this plan.`;
  }
  if (remaining === 1) return "Room for 1 more on your plan.";
  return `Room for ${remaining.toLocaleString()} more on your plan.`;
}

function buildKpis(data: TeacherBillingSubscriptionResponse, aiTitle: string): KpiItem[] {
  const { usage, limits, accessPhase } = data;
  const paid = accessPhase === "paid";

  return [
    {
      id: "cohorts",
      iconName: "graduation-cap",
      accent: { bg: "#1b4d2e", fg: "#fdf9f3" },
      title: "Live classes",
      used: usage.activeCohortsUsed,
      limit: limits.activeCohorts,
      unit: "active cohort",
      description:
        "Cohorts teaching right now. Concluded classes stay in your records and do not count toward this cap.",
      perk: headroomLine(usage.activeCohortsUsed, limits.activeCohorts, "active cohort") ?? undefined,
    },
    {
      id: "students",
      iconName: "person-running",
      accent: { bg: "#2a4d8f", fg: "#fdf9f3" },
      title: "Students enrolled",
      used: usage.activeStudentsUsed,
      limit: limits.activeStudents,
      unit: "student seat",
      description:
        "Roster seats filled across active cohorts. Each student counts once per cohort they are in.",
      perk:
        usage.activeStudentsUsed > 0
          ? `Teaching ${usage.activeStudentsUsed.toLocaleString()} student${usage.activeStudentsUsed === 1 ? "" : "s"} right now. ${headroomLine(usage.activeStudentsUsed, limits.activeStudents, "student seat") ?? ""}`.trim()
          : headroomLine(usage.activeStudentsUsed, limits.activeStudents, "student seat") ?? undefined,
    },
    {
      id: "lessons",
      iconName: "book-open",
      accent: { bg: "#7a3b8f", fg: "#fdf9f3" },
      title: "Lessons built",
      used: usage.activeLessonsUsed,
      limit: limits.activeLessons,
      unit: "lesson",
      description: paid
        ? "Activities in your library, ready to assign, remix, or publish to the community."
        : "Lesson drafts in your library during the trial. Upgrade for unlimited lessons.",
      perk: paid
        ? usage.activeLessonsUsed > 0
          ? `${usage.activeLessonsUsed.toLocaleString()} lesson${usage.activeLessonsUsed === 1 ? "" : "s"} and growing. Unlimited on Teacher Pro.`
          : "Unlimited lessons on your plan. Start with AI or build from scratch."
        : headroomLine(usage.activeLessonsUsed, limits.activeLessons, "lesson") ?? undefined,
    },
    {
      id: "courses",
      iconName: "diagram-project",
      accent: { bg: "#8f4d2a", fg: "#fdf9f3" },
      title: "Course blueprints",
      used: usage.activeCoursesUsed,
      limit: null,
      unit: "course",
      description:
        "Templates that sequence your lessons. Launch a new cohort from any blueprint in a few clicks.",
      perk:
        usage.activeCoursesUsed > 0
          ? `${usage.activeCoursesUsed.toLocaleString()} blueprint${usage.activeCoursesUsed === 1 ? "" : "s"} ready to deploy.`
          : "Create a course template to organize your first class path.",
    },
    {
      id: "playgrounds",
      iconName: "gamepad",
      accent: { bg: "#2a8f7a", fg: "#fdf9f3" },
      title: "Playgrounds",
      used: usage.playgroundProjectsUsed,
      limit: null,
      unit: "playground",
      description:
        "Sandbox projects for demos, tinkering, and quick experiments outside formal assignments.",
      perk:
        usage.playgroundProjectsUsed > 0
          ? `${usage.playgroundProjectsUsed.toLocaleString()} sandbox${usage.playgroundProjectsUsed === 1 ? "" : "es"} for you and your students to explore.`
          : "Spin up a playground to model an idea before turning it into a lesson.",
    },
    {
      id: "ai",
      iconName: "wand-magic-sparkles",
      accent: { bg: "#4d6b1b", fg: "#fdf9f3" },
      title: aiTitle,
      used: usage.aiLessonRequestsUsed,
      limit: limits.aiRequestsPerPeriod,
      unit: "AI request",
      description: paid
        ? "Draft lessons, refine activities, and generate course content. Resets each billing month."
        : "AI-assisted lesson and course drafts for your trial period.",
      perk: headroomLine(usage.aiLessonRequestsUsed, limits.aiRequestsPerPeriod, "AI request") ?? undefined,
    },
  ];
}

function BillingKpiCard({ item }: { item: KpiItem }) {
  const icon = lessonIconDefinition(item.iconName);
  const pct = usagePct(item.used, item.limit);
  const atLimit = item.limit != null && item.used >= item.limit;

  return (
    <article
      className={`teacher-billing-kpi-card${atLimit ? " teacher-billing-kpi-card--at-limit" : ""}`}
    >
      <div className="teacher-billing-kpi-card-head">
        <span
          className="teacher-billing-kpi-icon"
          style={{ background: item.accent.bg, color: item.accent.fg }}
          aria-hidden
        >
          {icon ? <FontAwesomeIcon icon={icon} /> : null}
        </span>
        <div className="teacher-billing-kpi-head-copy">
          <h3 className="teacher-billing-kpi-title">{item.title}</h3>
          <p className="teacher-billing-kpi-value">
            <span className="teacher-billing-kpi-value-used">{item.used.toLocaleString()}</span>
            {item.limit != null ? (
              <span className="teacher-billing-kpi-value-limit"> / {item.limit.toLocaleString()}</span>
            ) : (
              <span className="teacher-billing-kpi-value-unlimited"> total</span>
            )}
          </p>
        </div>
      </div>

      <p className="teacher-billing-kpi-description">{item.description}</p>

      {item.perk ? <p className="teacher-billing-kpi-perk">{item.perk}</p> : null}

      {item.limit != null ? (
        <div
          className="teacher-billing-kpi-bar"
          role="progressbar"
          aria-valuenow={pct ?? 0}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${item.title}: ${item.used} of ${item.limit}`}
        >
          <div className="teacher-billing-kpi-bar-fill" style={{ width: `${pct ?? 0}%` }} />
        </div>
      ) : (
        <div className="teacher-billing-kpi-bar teacher-billing-kpi-bar--unlimited" aria-hidden>
          <div className="teacher-billing-kpi-bar-fill teacher-billing-kpi-bar-fill--unlimited" />
        </div>
      )}
    </article>
  );
}

export function BillingKpiGrid({
  data,
  aiTitle,
}: {
  data: TeacherBillingSubscriptionResponse;
  aiTitle: string;
}) {
  const items = buildKpis(data, aiTitle);
  return (
    <section className="teacher-billing-kpi-grid" aria-label="Plan usage">
      {items.map((item) => (
        <BillingKpiCard key={item.id} item={item} />
      ))}
    </section>
  );
}
